import { openDB, deleteDB, IDBPDatabase } from 'idb';

import { Logger } from '../logger';
import { IFileType } from '../file-type';
import { FileMeta } from '../../input/FileInputNext';

const dbName = 'file-cache';

export class FileCache {
  private db: IDBPDatabase;

  constructor(
    private readonly logger: Logger,
    private readonly fileTypeChecker: IFileType,
  ) {}

  private async init() {
    if (!this.db) {
      this.db = await openDB('file-values', 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(dbName)) {
            db.createObjectStore(dbName);
          }
        },
      });
    }

    return this.db;
  }

  private resizeImage(
    blob: Blob,
    maxWidth: number,
    maxHeight: number,
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = function () {
        URL.revokeObjectURL(url);

        let newWidth = img.width;
        let newHeight = img.height;

        if (img.width > maxWidth || img.height > maxHeight) {
          const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
          newWidth = Math.floor(img.width * ratio);
          newHeight = Math.floor(img.height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob(
          (resizedBlob) => {
            if (resizedBlob) {
              resolve(resizedBlob);
            } else {
              reject(new Error('Failed to create resized blob'));
            }
          },
          'image/jpeg',
          0.8,
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const dataUrl = reader.result as string;
          const base64 = dataUrl.split(',')[1];

          if (!base64) {
            throw new Error('not a base64 url');
          }

          resolve(dataUrl);
        } catch (e) {
          reject(e);
        }
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  base64ToBlob(base64: string) {
    try {
      const parts = base64.split(',');
      const sliceSize = 512;

      const header = parts[0] || '';
      const base64Data = parts[1] || '';

      const mimeMatch = header.match(/:(.*?);/);

      if (!mimeMatch) {
        throw new Error('Could not extract MIME type from data URL');
      }

      const mimeType = mimeMatch[1];

      const byteCharacters = atob(base64Data);
      const byteArrays: Uint8Array[] = [];

      for (
        let offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
      ) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        byteArrays.push(new Uint8Array(byteNumbers));
      }

      return new Blob(byteArrays, { type: mimeType });
    } catch (error) {
      throw new Error(
        `Failed to convert base64 to blob: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  async save(blob: Blob) {
    try {
      const id = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) =>
            (c === 'x'
              ? (Math.random() * 16) | 0
              : (Math.random() * 4) | 8
            ).toString(16),
          );

      const db = await this.init();

      const base64 = await this.blobToBase64(blob);
      let tx = db.transaction(dbName, 'readwrite');
      let store = tx.objectStore(dbName);
      await store.put(base64, `${id}_full`);
      await tx.done;

      let preview = blob;

      if (blob && blob.type.startsWith('image/')) {
        preview = await this.resizeImage(blob, 300, 600);
      }

      const base64Preview = await this.blobToBase64(preview);
      tx = db.transaction(dbName, 'readwrite');
      store = tx.objectStore(dbName);
      await store.put(base64Preview, `${id}_preview`);
      await tx.done;

      return id;
    } catch (error) {
      this.logger.error(error, {
        tags: {
          vkdoc_error_type: 'idb',
        },
      });
      throw error;
    }
  }

  async get(id: string) {
    try {
      const db = await this.init();
      const tx = db.transaction(dbName, 'readonly');
      const store = tx.objectStore(dbName);
      const retrievedBlob = await this.base64ToBlob(
        await store.get(`${id}_full`),
      );
      await tx.done;
      return (retrievedBlob as Blob) || null;
    } catch (error) {
      this.logger.error(error, {
        tags: {
          vkdoc_error_type: 'idb',
        },
      });
      return null;
    }
  }

  async getPreview(id: string) {
    try {
      const db = await this.init();
      const tx = db.transaction(dbName, 'readonly');
      const store = tx.objectStore(dbName);
      const preview = await this.base64ToBlob(await store.get(`${id}_preview`));
      await tx.done;
      return (preview as Blob) || null;
    } catch (error) {
      this.logger.error(error, {
        tags: {
          vkdoc_error_type: 'idb',
        },
      });
      return null;
    }
  }

  async deleteDB() {
    try {
      await deleteDB('file-values');
    } catch (e) {}
  }

  private getImageResolution(
    file: Blob,
  ): Promise<{ width: number; height: number }> {
    const image: Promise<{ width: number; height: number }> = new Promise(
      (resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            resolve({ width: img.width, height: img.height });
          };
          img.onerror = reject;
          img.src = reader.result as string;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      },
    );

    return image;
  }

  async createFileMeta(blob: Blob, filename = ''): Promise<FileMeta> {
    const mimeType = await this.fileTypeChecker.fileTypeFromBlob(blob);

    if (mimeType.startsWith('image/')) {
      let width = 0;
      let height = 0;
      try {
        const resolution = await this.getImageResolution(blob);
        width = resolution.width;
        height = resolution.height;
      } catch (e) {}

      return {
        type: 'image',
        mimeType,
        size: blob.size,
        localCacheId: '',
        name: filename,
        width,
        height,
      };
    }

    return {
      type: 'general',
      mimeType,
      size: blob.size,
      localCacheId: '',
      name: filename,
    };
  }
}
